// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { CartLineModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { getLastPurchasedOn } from "@vanmeter/App/Store/Selectors/ProductSelectors";
import Badge, { BadgePresentationProps, BadgeTypeValues } from "@vanmeter/MobiusOverrides/Badge/Badge";
import { CarouselCardType } from "@vanmeter/XDL/types";
import { mergeWithCss } from "@vanmeter/XDL/Utilities";
import React, { useState } from "react";

export type PreviouslyPurchasedBadgeStyle = Pick<BadgePresentationProps, "textBadgeCss" | "textBadgeWrapperCss">;

interface OwnProps {
    product?: CartLineModel | ProductContextModel | ProductModel;
    extendedStyles?: PreviouslyPurchasedBadgeStyle;
    /**
     * When true, "Prev." and the date render on separate lines.
     * When false (default), both render inline on a single line.
     */
    displayBadgeInTwoLines?: boolean;
    view?: CarouselCardType;
}

export const previouslyPurchasedbadgeStyles: PreviouslyPurchasedBadgeStyle = {
    textBadgeCss: {},
    textBadgeWrapperCss: {},
};

const PreviouslyPurchasedBadge = ({ product, extendedStyles, displayBadgeInTwoLines = false, view }: OwnProps) => {
    const [styles] = useState(() => mergeWithCss(previouslyPurchasedbadgeStyles, extendedStyles));

    const language = useAppSelector(state => state.context.session.language);

    if (!product) {
        return null;
    }
    const productModel = "product" in product ? product.product : product;
    const lastPurchasedOn = getLastPurchasedOn(productModel, language) as string;

    if (!lastPurchasedOn) {
        return null;
    }

    return (
        <Badge
            type={BadgeTypeValues.Text}
            value={lastPurchasedOn}
            badgeTextRounding={0}
            {...styles}
            {...{ displayBadgeInTwoLines, view }}
        />
    );
};

export default PreviouslyPurchasedBadge;
